// Simple auth utility for frontend-only authentication
// In a real application, this would be handled by a backend service
interface AuthUser {
  username: string
  name: string
  role: string
}
interface LoginAttempt {
  timestamp: number
  ip: string // In a real app, this would be the actual IP
}
// Mock users database
const USERS = {
  admin: {
    username: 'admin',
    password: 'Eon@8888',
    name: 'Admin User',
    role: 'superadmin',
  },
  viewer: {
    username: 'viewer',
    password: 'view123',
    name: 'View User',
    role: 'user',
  },
  console: {
    username: 'console',
    password: 'console123',
    name: 'Console User',
    role: 'ops',
  },
  compliance: {
    username: 'compliance',
    password: 'comply123',
    name: 'Compliance User',
    role: 'compliance',
  },
  security: {
    username: 'security',
    password: 'secure123',
    name: 'Security User',
    role: 'security',
  },
}
// Authentication settings
const MAX_LOGIN_ATTEMPTS = 5
const LOCKOUT_DURATION = 15 * 60 // 15 minutes in seconds
const ATTEMPT_WINDOW = 30 * 60 * 1000 // 30 minutes in milliseconds
export const auth = {
  // Get the current user from localStorage
  getCurrentUser: (): AuthUser | null => {
    const userJson = localStorage.getItem('auth_user')
    if (!userJson) return null
    try {
      return JSON.parse(userJson) as AuthUser
    } catch (e) {
      return null
    }
  },
  // Login the user
  login: (
    username: string,
    password: string,
  ): { success: boolean; message?: string; remainingAttempts?: number } => {
    // Check if account is locked
    const lockoutUntil = localStorage.getItem('auth_lockout')
    if (lockoutUntil) {
      const lockTime = parseInt(lockoutUntil, 10)
      if (Date.now() < lockTime) {
        const remainingSeconds = Math.ceil((lockTime - Date.now()) / 1000)
        return {
          success: false,
          message: 'Account is locked. Too many failed attempts.',
        }
      } else {
        // Lockout period has expired
        localStorage.removeItem('auth_lockout')
        localStorage.removeItem('auth_attempts')
      }
    }
    // Check login attempts
    const attemptsJson = localStorage.getItem('auth_attempts')
    let attempts: LoginAttempt[] = []
    if (attemptsJson) {
      try {
        attempts = JSON.parse(attemptsJson)
        // Filter out old attempts
        attempts = attempts.filter(
          (a) => a.timestamp > Date.now() - ATTEMPT_WINDOW,
        )
      } catch (e) {
        attempts = []
      }
    }
    // Check if max attempts reached
    if (attempts.length >= MAX_LOGIN_ATTEMPTS) {
      // Lock the account
      const lockoutTime = Date.now() + LOCKOUT_DURATION * 1000
      localStorage.setItem('auth_lockout', lockoutTime.toString())
      return {
        success: false,
        message:
          'Too many failed login attempts. Your account has been temporarily locked.',
      }
    }
    // Check credentials
    const user = Object.values(USERS).find((u) => u.username === username)
    if (!user || user.password !== password) {
      // Add failed attempt
      attempts.push({
        timestamp: Date.now(),
        ip: 'client-ip', // In a real app, this would be the actual IP
      })
      localStorage.setItem('auth_attempts', JSON.stringify(attempts))
      const remainingAttempts = MAX_LOGIN_ATTEMPTS - attempts.length
      return {
        success: false,
        message: 'Invalid username or password.',
        remainingAttempts,
      }
    }
    // Successful login - clear attempts
    localStorage.removeItem('auth_attempts')
    localStorage.removeItem('auth_lockout')
    // Store user info
    const authUser: AuthUser = {
      username: user.username,
      name: user.name,
      role: user.role,
    }
    localStorage.setItem('auth_user', JSON.stringify(authUser))
    return { success: true }
  },
  // Logout the user
  logout: () => {
    localStorage.removeItem('auth_user')
  },
  // Get login attempt status
  getLoginStatus: () => {
    const lockoutUntil = localStorage.getItem('auth_lockout')
    if (lockoutUntil) {
      const lockTime = parseInt(lockoutUntil, 10)
      if (Date.now() < lockTime) {
        const remainingSeconds = Math.ceil((lockTime - Date.now()) / 1000)
        return {
          locked: true,
          lockoutTime: remainingSeconds,
        }
      } else {
        // Lockout period has expired
        localStorage.removeItem('auth_lockout')
        localStorage.removeItem('auth_attempts')
        return { locked: false }
      }
    }
    // Check remaining attempts
    const attemptsJson = localStorage.getItem('auth_attempts')
    if (attemptsJson) {
      try {
        const attempts = JSON.parse(attemptsJson).filter(
          (a: LoginAttempt) => a.timestamp > Date.now() - ATTEMPT_WINDOW,
        )
        if (attempts.length > 0) {
          return {
            locked: false,
            remainingAttempts: MAX_LOGIN_ATTEMPTS - attempts.length,
          }
        }
      } catch (e) {
        // Invalid JSON, ignore
      }
    }
    return { locked: false }
  },
}
